// Remote printing (owner, 2026-10-29): a till prints the KOTs and bills a
// device without a printer sends (a waiter's phone, a phone browser).
//
// Senders: a device that never had the Dhaba Print Service (a phone browser)
// asks the server with `print_remotely` (Send KOT, QR Accept, Print bill). A
// device that has (or had) it prints here first, and hands a job over with
// POST /print-requests only when it definitely did not queue: no printer here
// for it, the service unreachable, not paired, or a 4xx refusal. A timeout
// "may have printed": never handed over (the person checks the printer).
// Tills: on the silent `print_request` event, on load and reconnect, and every
// 45 s (GET /print-requests), a till that can print a request claims it
// (exactly one device wins), prints it, and reports the result.
import { api, ApiError, getSelectedContext } from "@/lib/api";
import {
  TPSConnectionError,
  TPSError,
  TPSPairingRequiredError,
  demoPrintingOff,
  getTpsToken,
  tps,
} from "@/lib/tps";
import { loadAssignments } from "@/hooks/useTPS";
import { sendToPrinter, type PrintTicket } from "@/lib/printJobs";
import { withCopies } from "@/lib/printCopies";
import { kotLabel, reportPrintedKots } from "@/lib/orderKots";
import type { PrintBillResponse } from "@/types/orders";

export type PrintRequestKind = "kot" | "bill";

/** One request, as GET /print-requests and the `print_request` event carry it. */
export interface PrintRequestItem {
  request_id: string;
  kind: PrintRequestKind;
  kot_id?: string | null;
  kot_number?: string | null;
  station_id?: string | null;
  station_name?: string | null;
  /** KOT: the station's printer (null: the till's default KOT printer). */
  printer_id?: string | null;
  order_number?: string | null;
  table_number?: string | null;
  requested_by?: string | null;
  requested_by_name?: string | null;
  expires_at?: string;
}

/** `print_request` realtime metadata (silent, not stored). */
export interface PrintRequestEventMeta {
  kind: PrintRequestKind;
  request_ids: string[];
  order_id: string;
  order_number: string | null;
  table_number: string | null;
  requested_by_id: string | null;
  requested_by_name: string | null;
  requests: PrintRequestItem[];
}

export interface PrintRequestClaim {
  request_id: string;
  kind: PrintRequestKind;
  /** This claim's nonce: the result carries it. */
  claim_token: string;
  order_number: string;
  table_number: string | null;
  requested_by_name: string | null;
  escpos_data: string;
  paper_width: number;
  copies: number;
  kot_number?: string | null;
  printer_id?: string | null;
}

export interface PrintRequestCreateResponse {
  created: number;
  requests: PrintRequestItem[];
  reason?: string | null;
}

// ---------------------------------------------------------------------------
// This device
// ---------------------------------------------------------------------------

const EVER_PAIRED_KEY = "dhaba_tps_ever_paired";

/** The Dhaba Print Service is paired in this browser now (it can print at all). */
export function hasPrintService(): boolean {
  const paired = !!getTpsToken();
  if (paired) rememberPaired();
  return paired;
}

/** This browser was paired with the print service (set on pairing). */
export function rememberPaired(): void {
  try {
    localStorage.setItem(EVER_PAIRED_KEY, "1");
  } catch {
    /* storage unavailable */
  }
}

/**
 * This browser never had the print service (a phone browser): its KOTs and
 * bills go straight to a till (`print_remotely`). One that had it and lost the
 * pairing prints here first: the print service says "Connect", and the job
 * goes to a till only after that definite failure.
 */
export function neverPaired(): boolean {
  if (hasPrintService()) return false;
  // Demo printing off: the job is skipped here, never handed to a till.
  if (demoPrintingOff()) return false;
  try {
    return localStorage.getItem(EVER_PAIRED_KEY) !== "1";
  } catch {
    return true;
  }
}

/**
 * The print service definitely did not queue this job: unreachable, not
 * paired, or it refused it (4xx). A timeout may have printed: false (never
 * hand it over: the person checks the printer, "Print again?").
 */
export async function definitelyNotQueued(ticket: PrintTicket | { accepted: boolean; outcome?: PrintTicket["outcome"] }): Promise<boolean> {
  if (ticket.accepted || !ticket.outcome) return false;
  const outcome = await ticket.outcome;
  if (outcome.kind !== "rejected") return false;
  const error = outcome.error;
  if (error instanceof TPSPairingRequiredError) return true;
  if (error instanceof TPSConnectionError) return error.reason !== "timeout";
  if (error instanceof TPSError) return error.status >= 400 && error.status < 500;
  return false;
}

/** Print-for-others switches of this device, per outlet (Printer Configuration). */
export interface RemotePrintSettings {
  kots: boolean;
  bills: boolean;
}

const SETTINGS_PREFIX = "dhaba_remote_print_";
const DEVICE_ID_KEY = "dhaba_device_id";

function settingsKey(): string {
  return `${SETTINGS_PREFIX}${getSelectedContext()?.outlet?.id ?? "default"}`;
}

/**
 * This device's switches. Unset, each is on when this device can print that
 * kind: the print service is paired and a KOT (bill) printer is assigned.
 */
export function loadRemotePrintSettings(): RemotePrintSettings {
  return effectiveRemotePrintSettings(loadAssignments(), hasPrintService());
}

/** The switches this device set itself (unset: the default applies). */
export function storedRemotePrintSettings(): Partial<RemotePrintSettings> {
  try {
    const stored = JSON.parse(localStorage.getItem(settingsKey()) || "{}");
    return stored && typeof stored === "object" ? stored : {};
  } catch {
    return {};
  }
}

/** The switches for these printer assignments (Printer Configuration shows them live). */
export function effectiveRemotePrintSettings(
  assignments: { kot_printer_id: string | null; bill_printer_id: string | null },
  paired: boolean,
): RemotePrintSettings {
  const stored = storedRemotePrintSettings();
  return {
    kots: typeof stored.kots === "boolean" ? stored.kots : paired && !!assignments.kot_printer_id,
    bills: typeof stored.bills === "boolean" ? stored.bills : paired && !!assignments.bill_printer_id,
  };
}

export function saveRemotePrintSetting(kind: keyof RemotePrintSettings, on: boolean): void {
  try {
    const current = JSON.parse(localStorage.getItem(settingsKey()) || "{}") ?? {};
    localStorage.setItem(settingsKey(), JSON.stringify({ ...current, [kind]: on }));
  } catch {
    /* storage unavailable: the default applies */
  }
}

/** A stable id for this browser (sent with a claim, for the record). */
export function deviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const id = `web-${crypto.randomUUID()}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    return "web";
  }
}

// ---------------------------------------------------------------------------
// Senders
// ---------------------------------------------------------------------------

/** Requests this device made (every tab): never claimed back here (it could not print them). */
const OWN_REQUESTS_KEY = "dhaba_own_print_requests";
const OWN_REQUEST_TTL_MS = 15 * 60_000;

function loadOwnRequests(): Record<string, number> {
  try {
    const stored = JSON.parse(localStorage.getItem(OWN_REQUESTS_KEY) || "{}");
    if (!stored || typeof stored !== "object") return {};
    const now = Date.now();
    return Object.fromEntries(
      Object.entries(stored as Record<string, number>).filter(([, at]) => typeof at === "number" && now - at < OWN_REQUEST_TTL_MS),
    );
  } catch {
    return {};
  }
}

export function rememberOwnRequests(ids: Array<string | null | undefined> | null | undefined): void {
  const fresh = (ids ?? []).filter((id): id is string => !!id);
  if (fresh.length === 0) return;
  const own = loadOwnRequests();
  for (const id of fresh) own[id] = Date.now();
  try {
    localStorage.setItem(OWN_REQUESTS_KEY, JSON.stringify(own));
  } catch {
    /* storage unavailable */
  }
}

function isOwnRequest(id: string): boolean {
  return id in loadOwnRequests();
}

/**
 * Hand KOTs this device could not print to the outlet's tills. Best effort:
 * never throws; returns the requests (empty with KDS-only routing).
 */
export async function requestRemoteKots(kotIds: Array<string | null | undefined>): Promise<string[]> {
  const ids = [...new Set(kotIds.filter((id): id is string => !!id))];
  if (ids.length === 0) return [];
  try {
    const res = await api.post<PrintRequestCreateResponse>("/api/v1/print-requests", { kind: "kot", kot_ids: ids });
    const requestIds = (res?.requests ?? []).map((r) => r.request_id);
    rememberOwnRequests(requestIds);
    return requestIds;
  } catch (error) {
    console.warn("Could not hand the KOTs to a till", error);
    return [];
  }
}

/** A send's KOT print job, and what the print service said (null: not tried). */
export interface KotPrintAttempt {
  kot_ids: string[];
  ticket: { accepted: boolean; outcome?: PrintTicket["outcome"] } | null;
}

/**
 * After printing a send's KOTs here: report the ones the print service took
 * (POST /kot/printed), hand to a till the ones that definitely did not print
 * (no printer here, never tried, or a definite failure), and leave alone the
 * ones that may have printed (a timeout: the person checks the printer).
 * Never throws. Returns the KOT ids handed over.
 */
export async function settleKotPrints(sentKotIds: Array<string | null | undefined>, attempts: KotPrintAttempt[]): Promise<string[]> {
  const printed = new Set<string>();
  const maybe = new Set<string>();
  const failed = new Set<string>();
  for (const attempt of attempts) {
    if (!attempt.ticket) continue;
    const target = attempt.ticket.accepted ? printed : (await definitelyNotQueued(attempt.ticket)) ? failed : maybe;
    for (const id of attempt.kot_ids) target.add(id);
  }
  const handOver = [...new Set(sentKotIds.filter((id): id is string => !!id))].filter(
    (id) => !printed.has(id) && !maybe.has(id),
  );
  await reportPrintedKots([...printed]);
  return handOver.length > 0 ? requestRemoteKots(handOver).then(() => handOver) : [];
}

/**
 * Hand a bill this device could not print to the outlet's tills: the
 * print-bill answer exactly as it came (nothing is billed or counted again).
 * Never throws; false when the server did not take it.
 */
export async function requestRemoteBill(orderId: string, bill: PrintBillResponse): Promise<boolean> {
  if (!bill.print_token || !bill.escpos_data) return false;
  try {
    const res = await api.post<PrintRequestCreateResponse>("/api/v1/print-requests", {
      kind: "bill",
      order_id: orderId,
      payload_b64: bill.escpos_data,
      paper_width: bill.paper_width ?? 80,
      copies: bill.copies ?? 1,
      print_count: bill.print_count,
      print_token: bill.print_token,
    });
    rememberOwnRequests((res?.requests ?? []).map((r) => r.request_id));
    return true;
  } catch (error) {
    console.warn("Could not hand the bill to a till", error);
    return false;
  }
}

// ---------------------------------------------------------------------------
// The till
// ---------------------------------------------------------------------------

/** Requests this device already took on (claimed, or lost the claim): never twice. */
const handled = new Set<string>();

/** Tests: forget what this device handled or asked for. */
export function resetRemotePrintState(): void {
  handled.clear();
  printerCache = null;
  try {
    localStorage.removeItem(OWN_REQUESTS_KEY);
  } catch {
    /* ignore */
  }
}

const PRINTER_CACHE_MS = 30_000;
let printerCache: { at: number; ids: Set<string> } | null = null;

/** The print service's printers on this device (cached briefly). */
export async function servicePrinterIds(): Promise<Set<string>> {
  if (printerCache && Date.now() - printerCache.at < PRINTER_CACHE_MS) return printerCache.ids;
  try {
    const res = await tps.getPrinters();
    printerCache = { at: Date.now(), ids: new Set(res.data.printers.map((p) => p.id)) };
  } catch {
    printerCache = { at: Date.now(), ids: new Set() };
  }
  return printerCache.ids;
}

/**
 * The printer this device prints the request on, or null when it must leave
 * it to another till. KOT: its station's printer when this print service has
 * it; a KOT without a station printer goes to the default KOT printer. Bill:
 * the bill printer.
 */
export async function printerFor(item: PrintRequestItem, settings = loadRemotePrintSettings()): Promise<string | null> {
  if (!hasPrintService()) return null;
  const assignments = loadAssignments();
  if (item.kind === "bill") return settings.bills ? assignments.bill_printer_id || null : null;
  if (!settings.kots) return null;
  if (item.printer_id) return (await servicePrinterIds()).has(item.printer_id) ? item.printer_id : null;
  return assignments.kot_printer_id || null;
}

export interface PrintedForOther {
  request_id: string;
  /** "Printed KOT 12 for Table 7 (sent by Ravi)". */
  message: string;
}

function printedMessage(claim: PrintRequestClaim): string {
  const where = claim.table_number ? `Table ${claim.table_number}` : `order ${claim.order_number}`;
  const what = claim.kind === "kot" && claim.kot_number ? kotLabel(claim.kot_number) : "the bill";
  const by = claim.requested_by_name ? ` (sent by ${claim.requested_by_name})` : "";
  return `Printed ${what} for ${where}${by}`;
}

/** This device prints for others now: paired, and a switch on. */
export function printsForOthers(settings = loadRemotePrintSettings()): boolean {
  return hasPrintService() && (settings.kots || settings.bills);
}

const RESULT_ATTEMPTS = 3;

async function reportResult(id: string, body: Record<string, unknown>): Promise<void> {
  for (let attempt = 1; attempt <= RESULT_ATTEMPTS; attempt += 1) {
    try {
      await api.post(`/api/v1/print-requests/${id}/result`, body);
      return;
    } catch (error) {
      // 409: the claim is no longer this till's (it lapsed): nothing to retry.
      if (error instanceof ApiError && error.status < 500) return;
      if (attempt === RESULT_ATTEMPTS) {
        console.warn("Could not report a remote print", error);
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
    }
  }
}

/**
 * Print, on this device, the requests it can print: claim each (another till
 * may win: then nothing), print through the print service with its copies,
 * report the result (retried; a print that may have gone through counts as
 * printed). The print service reports its own problems. Returns what printed.
 * Never throws.
 */
export async function printForOthers(items: PrintRequestItem[]): Promise<PrintedForOther[]> {
  const printed: PrintedForOther[] = [];
  const settings = loadRemotePrintSettings();
  for (const item of items) {
    const id = item.request_id;
    if (!id || handled.has(id) || isOwnRequest(id)) continue;
    const printerId = await printerFor(item, settings);
    if (!printerId) continue;
    handled.add(id);
    let claim: PrintRequestClaim;
    try {
      claim = await api.post<PrintRequestClaim>(`/api/v1/print-requests/${id}/claim`, { device_id: deviceId() });
    } catch (error) {
      // 409: another till has it (or it expired). Anything else: try again on the next list.
      if (!(error instanceof ApiError && error.status === 409)) handled.delete(id);
      continue;
    }
    const target = claim.kind === "kot" ? claim.printer_id || printerId : printerId;
    const label = claim.kind === "kot" && claim.kot_number ? kotLabel(claim.kot_number) : "Bill";
    let accepted = false;
    let notQueued = false;
    try {
      const ticket = await sendToPrinter(withCopies({ printer_id: target, data: claim.escpos_data }, claim.copies), { label });
      accepted = ticket.accepted;
      notQueued = await definitelyNotQueued(ticket);
    } catch {
      accepted = false;
    }
    // Only a definite failure goes to another till; a timeout may have printed.
    await reportResult(
      id,
      notQueued
        ? { claim_token: claim.claim_token, ok: false, error: "The print service did not take it" }
        : { claim_token: claim.claim_token, ok: true },
    );
    if (accepted) printed.push({ request_id: id, message: printedMessage(claim) });
  }
  return printed;
}

/** The pending requests a till coming online should still print. */
export async function fetchPendingPrintRequests(): Promise<PrintRequestItem[]> {
  const res = await api.get<{ requests: PrintRequestItem[] }>("/api/v1/print-requests?status=pending");
  return Array.isArray(res?.requests) ? res.requests : [];
}

/** The requests of a `print_request` event, with its order's table and sender. */
export function itemsFromEvent(meta: Partial<PrintRequestEventMeta> | null | undefined): PrintRequestItem[] {
  if (!meta || !Array.isArray(meta.requests)) return [];
  return meta.requests.map((r) => ({
    ...r,
    kind: r.kind ?? meta.kind ?? "kot",
    order_number: r.order_number ?? meta.order_number ?? null,
    table_number: r.table_number ?? meta.table_number ?? null,
    requested_by_name: r.requested_by_name ?? meta.requested_by_name ?? null,
  }));
}
